import { formatInr } from "@/utils/basicFunctions.util";
import { useState } from "react";
import type { CounterCustomer } from "../counterDemo.type";

interface UseCounterDemoInput {
  customers: CounterCustomer[];
  initialBill: number;
  rule: { discountFrom: number; discount: number; cashbackRate: number };
  meta: string;
  newMeta: string;
  noReward: string;
  message: string;
  savedSuffix: string;
}

// Everything on the receipt, the profile and the WhatsApp message is derived
// from the chosen customer and the bill, as in the preview's `[data-ct]`.
export function useCounterDemo({
  customers,
  initialBill,
  rule,
  meta,
  newMeta,
  noReward,
  message,
  savedSuffix,
}: UseCounterDemoInput) {
  const initialState = { customerKey: customers[0].key, bill: initialBill };
  const [state, setState] = useState(initialState);

  const selectCustomer = (customerKey: CounterCustomer["key"]) =>
    setState((state) => ({ ...state, customerKey }));
  const setBill = (bill: number) => setState((state) => ({ ...state, bill }));

  const customer =
    customers.find((item) => item.key === state.customerKey) ?? customers[0];
  const discount = state.bill >= rule.discountFrom ? rule.discount : 0;
  const pay = state.bill - discount;
  const earn = Math.round(pay * rule.cashbackRate);

  const metaText =
    customer.key === "new"
      ? newMeta
      : meta
          .replace("{visits}", customer.visits)
          .replace("{last}", customer.lastVisit);
  const chatText =
    message
      .replace("{name}", customer.name)
      .replace("{earn}", formatInr(earn)) +
    (discount ? savedSuffix.replace("{disc}", formatInr(discount)) : "");

  return {
    customer,
    bill: state.bill,
    selectCustomer,
    setBill,
    billText: formatInr(state.bill),
    discountText: discount ? `−${formatInr(discount)}` : noReward,
    payText: formatInr(pay),
    earnText: `+${formatInr(earn)}`,
    walletText: formatInr(customer.wallet + earn),
    metaText,
    chatText,
  };
}
